import { useState, type FormEvent } from 'react';

const FORM_ENDPOINT = 'https://readdy.ai/api/form/dah9193p14a1h3mr6fog';

export default function SubscribeForm({ compact = false }: { compact?: boolean }) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setMessage('订阅成功，福利上新会第一时间提醒你。');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    setStatus('loading');
    setMessage('');

    const email = String(formData.get('email') || '').trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setMessage('请填写正确的邮箱地址，方便我们发送提醒。');
      return;
    }

    try {
      const payload = new URLSearchParams();
      formData.forEach((value, key) => {
        payload.append(key, String(value));
      });

      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMessage = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && code === 'OK') {
        setStatus('success');
        setMessage('订阅成功，福利上新会第一时间提醒你。');
        form.reset();
      } else {
        setStatus('error');
        setMessage(
          typeof serverMessage === 'string' && serverMessage.length > 0
            ? serverMessage
            : '提交失败，请稍后重试。',
        );
      }
    } catch {
      setStatus('error');
      setMessage('网络好像不太稳定，请稍后重试。');
    }
  };

  return (
    <div id="subscribe" className="w-full scroll-mt-24">
      <form
        id="subscribe-form"
        data-readdy-form
        onSubmit={handleSubmit}
        className={`flex flex-col gap-3 ${compact ? '' : 'sm:flex-row sm:items-center'}`}
        noValidate
      >
        <div className={`flex flex-col sm:flex-row gap-3 ${compact ? '' : 'flex-1'}`}>
          <label htmlFor="subscribe-email" className="sr-only">
            邮箱地址
          </label>
          <input
            id="subscribe-email"
            type="email"
            name="email"
            autoComplete="email"
            placeholder="输入邮箱，接收福利上新提醒"
            className="flex-1 min-w-0 px-4 py-3 rounded-xl bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors"
          />
          <input
            type="text"
            name="website_alt"
            className="form-hp-field"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />
          <button
            type="submit"
            disabled={status === 'loading'}
            className="shrink-0 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 disabled:opacity-70 transition-colors cursor-pointer"
          >
            <i className={status === 'loading' ? 'ri-loader-4-line animate-spin' : 'ri-notification-3-line'}></i>
            {status === 'loading' ? '提交中…' : '订阅提醒'}
          </button>
        </div>
      </form>
      {message && (
        <p
          className={`mt-3 text-sm flex items-center gap-2 ${
            status === 'success' ? 'text-secondary-700' : 'text-primary-700'
          }`}
          role="status"
        >
          <i className={status === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
          {message}
        </p>
      )}
    </div>
  );
}